import { Button } from '@client/components/ui/button';
import { MailCheck } from 'lucide-react';
import {
  cn,
  deleteOauthConnectionByAdvisorIdRequest,
  DestructiveActionDialog,
  OAUTH_REDIRECT_FROM_PAGE_KEY,
  selectSessionAdvisor,
} from '..';
import { OAuthRedirectPage } from '@client/components/OAuthCallback';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { getOauthProviderIcon } from '@client';

type OAuthConnectionProps = {
  provider: OAuthProvider;
  redirectPage?: OAuthRedirectPage;
};

export function OAuthConnection({
  provider,
  redirectPage = OAuthRedirectPage.ADVISOR_PROFILE,
}: OAuthConnectionProps) {
  const advisor = useSelector(selectSessionAdvisor);
  const dispatch = useDispatch();
  const oauthSettings = advisor.advisorOauthSettings[provider];
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const providerDisplayName = provider === 'google' ? 'Google' : 'Microsoft';

  // Check if any other provider is connected
  const otherProvider = provider === 'google' ? 'microsoft' : 'google';
  const otherProviderSettings = advisor.advisorOauthSettings[otherProvider];
  const isOtherProviderConnected = !!(
    otherProviderSettings?.userEmail && otherProviderSettings?.createdAtDateTime
  );

  const onDisconnect = () => {
    dispatch(
      deleteOauthConnectionByAdvisorIdRequest({
        advisorId: advisor.advisorId,
        provider,
      }),
    );
    setShowDisconnectDialog(false);
  };

  return (
    <div className="flex flex-col gap-2">
      {oauthSettings.userEmail && oauthSettings.createdAtDateTime ? (
        <div className="border-primary mb-3 flex items-center border-l-4 px-3 py-2 text-sm text-gray-700">
          <MailCheck className="text-primary mr-2 h-5 w-5" />
          <div className="flex-1">
            <p className="font-medium">Connected to {oauthSettings.userEmail}</p>
            <p className="text-xs text-gray-500">
              Connected on {new Date(oauthSettings.createdAtDateTime).toLocaleDateString()}
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowDisconnectDialog(true)}
            className="bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700"
          >
            Disconnect
          </Button>
        </div>
      ) : null}

      {/* Only show the sign-in button if this provider is connected OR no other provider is connected */}
      {(oauthSettings.userEmail || !isOtherProviderConnected) && (
        <div>
          <Button
            type="button"
            onClick={() => {
              localStorage.setItem(OAUTH_REDIRECT_FROM_PAGE_KEY, redirectPage);
              window.location.href = oauthSettings.consentUrl;
            }}
            variant="outline"
            className={cn('flex w-full items-center justify-start gap-2 md:w-[210px]')}
          >
            <div className="flex justify-center">{getOauthProviderIcon(provider)}</div>
            <span className="text-center">
              {oauthSettings.userEmail
                ? `Update Connection`
                : `Sign in with ${providerDisplayName}`}
            </span>
          </Button>
        </div>
      )}

      <DestructiveActionDialog
        open={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        onConfirm={onDisconnect}
        title={`Disconnect ${providerDisplayName} Account`}
        description={
          <>
            Are you sure you want to disconnect your {providerDisplayName} account (
            {oauthSettings.userEmail})? This will disable email sending functionality from your{' '}
            {providerDisplayName} account. You can reconnect at any time.
          </>
        }
        confirmButtonText="Disconnect"
      />
    </div>
  );
}
